/**
 * The wire, and the only file in `src/data/` that names a URL.
 *
 * The web client speaks to `/api/sync` and to nothing else. The REST routes
 * stay — they are the agent surface — but a screen reading from a local mirror
 * has exactly one way to reconcile it, and two would be two chances to disagree.
 */

import type { Anchors, Creator, Due, Item, ItemPatch, List, ListKind, ListOrder, ListPatch } from "../lib/api.js";
import { ApiError, request, send } from "../lib/http.js";
import { credentialHeaders, type Listed, type ShareMode, type ShareVisitor } from "../lib/sharing.js";
import { visitorKey, type StoreScope } from "./scopes.js";
import type { Repeat } from "../lib/recurrence.js";
import type { OccurrenceCommand } from "../lib/occurrence.js";
import type { ImportMutation } from "./imports.js";
import type {
  ActionDetailView, ActionMetaInput, ActivityPage, ActivityStamp, SyncActionReceipt, SyncGroupInput, SyncGroupReply,
} from "../../api/src/shared/activity.js";
export interface OperationContext { operationId?: string; expectedRevision?: number }
/** The envelope a whole action travels with (g107). Absent from a folded record's mutation. */
export interface ActionContext { action?: ActionMetaInput }

/**
 * Mirrors `MAX_BATCH` in `api/src/routes/schemas.ts`. Exceeding it is a 400 on
 * the whole request rather than a per-mutation verdict, so the queue splits
 * rather than discovering the cap the hard way.
 */
export const MAX_BATCH = 500;
export const MAX_SYNC_BODY_BYTES = 10 * 1024 * 1024;

export type Mutation =
  | (ImportMutation & ActionContext)
  | ({ op: "list.upsert"; id: string; name: string; kind: ListKind } & ActionContext)
  /**
   * A name or a kind, or (g81) how the list is ordered. An ordering travels
   * with nothing else in its patch and alone in its request: switching it on
   * fills blank categories on the server, charged by the rows it writes.
   */
  | ({ op: "list.patch"; id: string; patch: ListPatch | { orderBy: ListOrder | null } } & ActionContext)
  | ({ op: "list.delete"; id: string } & ActionContext)
  | ({ op: "list.move"; id: string } & Anchors & ActionContext)
  | ({
      op: "item.upsert";
      id: string;
      listId: string;
      text: string;
      done: boolean;
      quantity: string | null;
      category: string | null;
      note: string | null;
      due: Due | null;
      repeat?: Repeat | null;
    } & Anchors & OperationContext & ActionContext)
  | ({ op: "item.patch"; id: string; patch: ItemPatch } & OperationContext & ActionContext)
  | ({ op: "item.complete" | "item.undoCompletion"; id: string } & OccurrenceCommand & ActionContext)
  /**
   * Moves an item within its list, and deliberately is not a
   * patch. A patch resolves per field by arrival order, which is right for text
   * and wrong for position: a device offline for an hour would otherwise win
   * simply by arriving last, with a position computed against a list that has
   * changed underneath it.
   */
  | ({ op: "item.move"; id: string } & Anchors & OperationContext & ActionContext)
  | ({ op: "item.delete"; id: string } & ActionContext);

interface Verdict {
  index: number;
  op: Mutation["op"];
  id: string;
  /** What the server recorded for a mutation that carried `action` (g107). Only on an accepted one. */
  action?: SyncActionReceipt;
}

export type MutationResult =
  | (Verdict & { status: "applied"; op: "list.import"; list: List; items: Item[] })
  | (Verdict & { status: "applied"; list: List })
  | (Verdict & { status: "applied"; item: Item })
  | (Verdict & { status: "rejected"; reason: string; message: string; list?: List });

export interface Delta {
  cursor: number;
  lists: List[];
  items: Item[];
  /**
   * Who made the rows here, and anyone renamed since the cursor who made a
   * row this account holds (g68). Absent only from a server older than g68.
   */
  creators?: Creator[];
  /** A shared list's items that left it for another of the owner's lists (g70). */
  departed?: string[];
  /** A shared list's answer says who is looking and what they may do (g70). */
  scope?: ReplyScope;
  /**
   * Every shared list the account added (g132), whole, on an account's reply
   * from a server that has them. Absent means the reply says nothing of them,
   * never that there are none.
   */
  added?: AddedEntry[];
  /**
   * Present while the server records history (g107), naming the version of
   * `action` it records. The mirror remembers it, and the queue keeps whole
   * actions only while the last reply said so.
   */
  recording?: unknown;
  /** What the server said of each group the push spoke of (g108). */
  groups?: SyncGroupReply[];
  /**
   * Where the owner's history stands, on an owner's reply while the server
   * lets it be read (g109). The mirror keeps it, and a device keeps no history
   * pages from a reply without one (g110). A share link's reply never has it.
   */
  activity?: ActivityStamp;
  /**
   * Each group this push spoke of, with the server's answer and what was said
   * of it. Set by the engine, never sent by the server: the mirror amends
   * each ledger from it in the transaction that stores the reply.
   */
  acks?: GroupAck[];
  /**
   * The added lists this push removed (g132). Set by the engine, never sent by
   * the server: a reply that carries `added` clears them from what waits.
   */
  removedSent?: string[];
  /**
   * The cursor this delta was read from. Set by the engine, never sent by the
   * server: a full download (0) is what may mark the mirror as holding every
   * list's `sharing` (g71).
   */
  from?: number;
}

/** What the engine said of a group in a push, and what the reply said back (g108). */
export interface GroupAck {
  id: string;
  status: SyncGroupReply["status"];
  /** The children this push said would not be sent. */
  notSent: readonly number[];
}

export interface ReplyScope {
  listId: string;
  permission: ShareMode;
  generation: string;
  visitor: ShareVisitor;
  writable: boolean;
  /** Whether the list is in the visitor's Lists (g132). Absent from an older server. */
  listed?: Listed;
}

/**
 * A shared list the account added to its Lists (g132), as its own sync sends
 * it. A live one says what the Lists row shows; an ended one says nothing of
 * the list, since the link no longer lets the visitor read it.
 */
export type AddedEntry =
  | { grantId: string; mode: ShareMode; addedAt: number; state: "live"; name: string; kind: ListKind; sharedBy: string | null }
  | { grantId: string; mode: ShareMode; addedAt: number; state: "ended" };

export interface PushReply extends Delta {
  results: MutationResult[];
}

/** A pull with nothing to send. `since=0` is a full download, tombstones and all. */
export function pull(since: number): Promise<Delta> {
  return request<Delta>(`/api/sync?since=${since}`);
}

/**
 * Push and pull in one round trip. `since` is always sent, so the reply always
 * carries a delta — and the server reads it *after* applying the batch, so the
 * client's own writes come back with the timestamps the server gave them.
 */
export function push(since: number, mutations: Mutation[], groups: readonly SyncGroupInput[] = [], removeAdded: readonly string[] = []): Promise<PushReply> {
  return send<PushReply>("POST", "/api/sync", { ...body(since, mutations, groups), ...(removeAdded.length ? { removeAdded } : {}) });
}

/** A push's body. `groups` is left out where there is none, so a push that speaks of no group is the bytes it always was. */
function body(since: number, mutations: Mutation[], groups: readonly SyncGroupInput[]) {
  return { since, mutations, ...(groups.length ? { groups } : {}) };
}

/** Who a page's account mirror belongs to, as the page believes (g71). */
export const ACCOUNT_HEADER = "X-Trayflux-Account";
/** Who a shared page queued its writes as (g71). */
export const VISITOR_HEADER = "X-Trayflux-Visitor";

/**
 * A pull that hangs is a refresh that never comes back: the engine runs one
 * pass at a time, and an open list's timer waits on it (g71). Bounded, it
 * becomes a failed attempt that backs off like any other.
 */
export const PULL_TIMEOUT_MS = 30_000;

function bounded(): { signal?: AbortSignal } {
  return typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function"
    ? { signal: AbortSignal.timeout(PULL_TIMEOUT_MS) }
    : {};
}

export interface Wire {
  pull(since: number): Promise<Delta>;
  /**
   * `removeAdded` names shared lists the account took out of its Lists (g132).
   * Only an account's wire sends it; a shared scope's ignores it.
   */
  push(since: number, mutations: Mutation[], groups?: readonly SyncGroupInput[], removeAdded?: readonly string[]): Promise<PushReply>;
  /** An account's history (g110). A shared scope's wire has none: a link never reads history. */
  history?: HistoryWire;
}

/** Which history a page is of (g110): the account's, one list's or one item's. */
export type HistoryQuery = { list?: string; item?: string; cursor?: string };

export interface HistoryWire {
  page(query: HistoryQuery): Promise<ActivityPage>;
  action(id: string, cursor?: string): Promise<ActionDetailView>;
}

/** A page's query string, in a fixed order. Only what is set is sent: the server refuses an empty parameter. */
export function historyPath(query: HistoryQuery): string {
  const params = new URLSearchParams();
  if (query.list) params.set("list", query.list);
  if (query.item) params.set("item", query.item);
  if (query.cursor) params.set("cursor", query.cursor);
  const text = params.toString();
  return `/api/sync/activity${text ? `?${text}` : ""}`;
}

/**
 * A shared list as the server sends it has no `orderKey`: that is its place
 * among the owner's lists, which a visitor has no business knowing (g70). The
 * mirror sorts lists by it, and a scope holds exactly one.
 */
function placed(delta: Delta): Delta {
  return { ...delta, lists: delta.lists.map((row) => ({ ...row, orderKey: row.orderKey ?? "a0" })) };
}

/**
 * The wire for one scope. An account's copy names its account, so a page whose
 * cookie now belongs to someone else is refused rather than answered. A shared
 * list's copy sends its link in the share header and nowhere else, and names
 * the visitor who queued what it pushes.
 */
export function transportFor(scope: StoreScope): Wire {
  if (scope.kind === "account") {
    const headers = { [ACCOUNT_HEADER]: scope.userId };
    return {
      pull: (since) => request<Delta>(`/api/sync?since=${since}`, { headers, ...bounded() }),
      push: (since, mutations, groups = [], removeAdded = []) => request<PushReply>("/api/sync", {
        method: "POST", headers,
        body: JSON.stringify({ ...body(since, mutations, groups), ...(removeAdded.length ? { removeAdded } : {}) }),
      }),
      // The account header here too: a page whose cookie now belongs to someone else reads nobody's history.
      history: {
        page: (query) => request<ActivityPage>(historyPath(query), { headers, ...bounded() }),
        action: (id, cursor) => request<ActionDetailView>(
          `/api/sync/activity/${encodeURIComponent(id)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
          { headers, ...bounded() },
        ),
      },
    };
  }
  const share = credentialHeaders(scope.credential);
  return {
    pull: async (since) => placed(await request<Delta>(`/api/sync?since=${since}`, { headers: share, ...bounded() })),
    push: async (since, mutations, groups = []) => {
      // The store refuses these before they are queued; this is the last line.
      if (scope.mode !== "edit") throw new ApiError(403, "share_read_only", "This link can only view the list.");
      const reply = await request<PushReply>("/api/sync", {
        method: "POST",
        headers: { ...share, [VISITOR_HEADER]: visitorKey(scope) },
        body: JSON.stringify(body(since, mutations, groups)),
      });
      return placed(reply) as PushReply;
    },
  };
}
